import { formatNumber } from "./lang.ts";
import type { UiStrings } from "./vi.ts";

/**
 * English twin of `vi.ts`, same keys and the same house style: playful and
 * warm, plain words only, no dashes, middle dots, ellipses, slashes, plus or
 * times signs.
 */
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const en: UiStrings = {
  credit: "Created by nanodev.io.vn",
  loading: "Opening the city gates, hang on a sec!",
  pick: {
    title: "Who do you want to be today?",
    subtitle: "You can change your mind any time in Settings.",
    random: "Surprise me!",
    restore: "Got a code? Bring your journey back",
  },
  hud: {
    shop: "Shop",
    wardrobe: "Wardrobe",
    leaderboard: "Ranking",
    settings: "Settings",
    achievements: "Achievements",
    pending: "waiting to sync",
    firstHint: "Tap the Sorting House, a mountain of stuff is waiting for you!",
    guest: "Little friend",
  },
  lobby: {
    current: (n) => `You are on level ${n} of 100`,
    play: "Keep playing",
    playLevel: (n) => `Play level ${n}`,
    playNext: (n) => `Play level ${n} next`,
    chapter: (n) => `Chapter ${n}`,
    chapterName: (n, name) => `Chapter ${n}, ${name}`,
    chapterStars: (n) => `${n} of 30`,
    needNetwork: "This chapter needs the internet the first time it loads. Hop on wifi for us!",
    allDone: "You beat all 100 levels! That is amazing!",
  },
  construction: {
    // Short words: this also goes on the little signposts in the city.
    title: "Coming soon",
    body: "The builders are busy hammering away. This house opens soon, come back later!",
    maintenance: "Tidying up",
    maintenanceBody: "We are polishing this house until it sparkles. Your progress is safe and sound.",
  },
  puzzle: {
    level: (n) => `Level ${n}`,
    moves: (used, max) => (max !== null ? `${plural(Math.max(0, max - used), "move")} left` : plural(used, "move")),
    target: (n) => `Target ${n}`,
    undo: "Undo",
    hint: "Hint",
    restart: "Restart",
    pauseTitle: "Little break",
    resume: "Keep playing",
    quit: "Back to the city",
    outOfMoves: "Oops, out of moves! One more round?",
    retry: "Try again",
    tutorialOk: "Got it!",
    hintCost: "Using a hint means this level can only get 1 star. Still want it?",
    hintFree: (n) => `You have ${plural(n, "free hint")} left on this level.`,
    hintYes: "Yes please!",
    hintLeft: (n) => `${plural(n, "free hint")} left`,
    hintPrice: (n) => `Hint for ${n} gold`,
    hintBuyTitle: "No free hints left",
    hintBuyBody: (price) => `Trade ${price} gold for one more hint? The server counts the gold, safe and sound.`,
    hintBuy: (price) => `Trade ${price} gold`,
    hintWallet: "Your wallet",
    hintPoor: "Not enough gold yet, clear a few more levels and you will have it.",
    hintOffline: "You need the internet to trade gold for a hint.",
    hintBought: "One more hint is yours!",
    hintNo: "No thanks, I will figure it out",
    hintUndo: "Little hint, try undoing your last step!",
    hintNone: "Even the hint bulb is stumped. Try a different move!",
    loadFailed: "This level got stuck somewhere on the internet. Check your connection and try again!",
    finale: "Boss level",
    difficulty: ["", "Super easy", "Easy", "A bit tricky", "Tricky", "Super tricky"],
  },
  result: {
    title: "Great job!",
    titleByStars: ["", "Level cleared!", "Great job!", "Flawless!"],
    chapterDone: (n) => `Chapter ${n} done!`,
    combo: (n) => `Longest streak ${n} in a row`,
    time: (text) => `Finished in ${text}`,
    newRecord: "Brand new record!",
    gold: (n) => `You got ${n} gold`,
    pendingOffline: "Saved on this device, it syncs as soon as you are online.",
    pending: "Waiting for the server to nod, just a sec!",
    confirmed: "The server nodded!",
    improved: (from, to) => `From ${plural(from, "star")} up to ${plural(to, "star")}!`,
    replay: "You already cleared this one, the first clear prize only comes once.",
    next: "Next level",
    again: "Play again",
    city: "Back to the city",
    saveFailed: "Oops, the result did not save on this device. Please try again!",
    needsUpdate: "This level needs a little update",
  },
  shop: {
    title: "Magic Shop",
    wardrobe: "Your Wardrobe",
    owned: "Owned",
    wearing: "Wearing",
    notEnough: "A bit short on gold",
    buy: (price) => `Buy for ${price} gold`,
    wear: "Wear",
    remove: "Take off",
    takeOff: "Wearing it, tap to take off",
    confirm: (name, price) => `Take home ${name} for ${price} gold?`,
    confirmYes: "Take it home!",
    offline: "You need the internet to shop. Things you own you can try on and wear freely!",
    disabled: "The shop is taking a nap, come back later!",
    bought: (name) => `You bought ${name}! So cute!`,
    empty: "This shelf is empty, new stuff is on the way!",
    preview: "Try on",
    rarity: { common: "Common", rare: "Rare", epic: "Epic" },
    wishlist: "Wish",
    wishlistSet: "Pinned as your dream item. Check the corner of the screen to track it!",
    goalLeft: (n) => `Just ${formatNumber(n)} more gold to go`,
    goalReady: "You have enough gold, grab it!",
    almost: "Almost there",
    revealTitle: "Something new!",
    revealWearing: "You are wearing it now",
    revealOk: "So pretty!",
  },
  achievements: {
    title: "Achievements",
    unlocked: "New achievement!",
    unlockedMany: (n) => `${n} new achievements at once!`,
    claim: (gold) => `Claim ${gold}`,
    claimed: "Claimed",
    earned: "Earned",
    badgeOnly: "Badge",
    progress: (current, target) => `${current} of ${target}`,
    summary: (done, total) => `You earned ${done} of ${total} achievements`,
    rewardLine: (gold) => `${gold} gold is waiting for you in Achievements`,
    waitSync: "Your progress is still on its way to the server. Wait a few minutes and try again!",
    offline: "You need the internet to claim gold rewards.",
    deviceNote: "Badges timed by this device's clock are just for collecting, they come without gold.",
  },
  leaderboard: {
    title: "Ranking",
    you: (rank) => (rank ? `You are number ${rank}` : "No rank yet. Go get your first star!"),
    beyond: "You are chasing the leaders. Keep going, you are almost there!",
    stale: (time) => `The board is waiting to refresh, saved at ${time}.`,
    offline: "You are offline, here is the saved board for now.",
    disabled: "The ranking is on vacation.",
    empty: "The board is empty. You can be the first flag on top!",
    stars: "stars",
    levels: "levels",
    summary: (stars, levels) => `${plural(stars, "star")}, ${plural(levels, "level")}`,
    houseLine: (levels, stars) => `${plural(levels, "level")}, ${plural(stars, "star")}`,
    houses: "House progress",
    up: (n) => `Up ${plural(n, "place")}!`,
    down: (n) => `Down ${plural(n, "place")}, let's win them back!`,
    update: "Update my rank",
    updating: "Updating",
    updated: "Done! The board has your newest name and progress.",
    updatePending: "A little data is still on its way, wait a few seconds and tap again.",
    updateFailed: "Could not update right now, try again in a bit.",
  },
  settings: {
    title: "Settings",
    sound: "Sound",
    music: "Music",
    musicSoon: "Coming soon",
    language: "Language",
    app: "Android app",
    getApk: "Get the Android app",
    getApkNote: "Install it once, and from then on the game updates itself whenever there is something new.",
    vibration: "Vibration",
    name: "Display name",
    save: "Save",
    nameSaved: "Name saved! Sounds great!",
    nameRejected: "That name does not work yet, try another one.",
    character: "Change character",
    backup: "Save my journey",
    backupBody:
      "Make a secret recovery code to take your journey to another device. Keep it super secret, anyone with the code can use your journey!",
    backupNeedSync: "Get online so your progress can sync before making a code.",
    newCode: "Make a new code, the old one stops working",
    codeTitle: "Your recovery code",
    codeOnce: "This code shows only once. Copy it and keep it somewhere safe!",
    copy: "Copy",
    copied: "Copied!",
    qr: "Show QR code",
    restore: "Restore my journey",
    restoreBody: "Enter your recovery code here. The journey on this device will be replaced by the one in the code.",
    restorePlaceholder: "Paste your recovery code here",
    restoreConfirm: "Really replace the journey on this device?",
    restoreYes: "Replace it!",
    restored: "Your journey is back home!",
    honest: "If you never synced, or lost both the code and this device's data, the journey cannot be rescued.",
    storageBlocked: "The browser is blocking saving on this device, closing the page could lose your progress.",
    sync: "Sync",
    syncState: {
      idle: "All synced",
      syncing: "Syncing, just a sec",
      offline: "You are offline, but you can keep playing",
      error: "We will try again in a bit",
      disabled: "Cloud sync is taking a break",
      session_lost: "Your session expired. Use your recovery code to reconnect",
    },
    pendingCount: (n) => `${plural(n, "level")} waiting to sync`,
    rejectedCount: (n) => `${plural(n, "level")} need a little update`,
    about: "About",
    version: (v) => `Version ${v}`,
    tag: (tag) => `Player tag ${tag}`,
    privacy: "No account needed. We never ask for an email or phone number.",
  },
  update: {
    ready: "A new update is here!",
    apply: "Update",
    later: "Later",
    apk: "A new app version is out",
    apkBody: "Get the new version for even more fun. Your progress stays just as it is.",
    download: "Get the new version",
    title: "Updates",
    running: (version) => `Running version ${version}`,
    check: "Check for updates",
    checking: "Asking the server what is new, hang on",
    latest: "You have the newest version!",
    downloading: "Downloading the update",
    readyBody: "The new version is already on your device. Tap update and you are done, your progress stays safe.",
    applyNow: "Update now",
    apkReady: (version) => `New app version ${version} is out`,
    failed: "Could not reach the server. Try again when you are online.",
    auto: "When you are online the game grabs new versions by itself, they kick in next time you open it.",
  },
  getApp: {
    title: "Android app",
    body: "Smoother play, full screen, and it updates itself whenever there is a new version.",
    step1: "Make a recovery code to carry your journey into the app.",
    step2: "Download the APK file and open it to install. If your phone asks, allow installing from this source.",
    step3: "Open the app, pick Restore my journey and enter your code.",
    download: (size) => (size ? `Download APK, ${size}` : "Download APK"),
    unavailable: "The APK is still baking in the oven, come back later!",
    makeCode: "Make a recovery code",
    scan: "On a computer? Scan this code with your Android phone's camera to download it.",
    android: "The APK file only installs on Android phones.",
  },
  install: {
    title: "Add to Home Screen",
    body: "Want full screen play, faster loading and safer progress on your iPhone or iPad? Here is how!",
    step1: "Tap the Share button in the Safari toolbar",
    step2: "Pick Add to Home Screen",
    step3: "Open the game from the new icon on your home screen",
    continue: "Keep playing in Safari",
    note: "Safari may clean up web data you have not used in a while. The home screen version is much safer.",
  },
  preload: {
    title: "Getting the city ready",
    body: "Downloading all the pictures first so the game runs smoothly even on weak internet.",
    progress: (done, total) => `Downloaded ${done} of ${total} files`,
    failed: "A few files wandered off and are not back yet.",
    retry: "Try again",
    skip: "Play now",
  },
  wait: "Hang on a sec",
  starsLabel: (n) => plural(n, "star"),
  rotate: "Turn your phone upright to play!",
  contextLost: "The graphics dozed off. Tap to reload, your progress is saved.",
  reload: "Reload",
  close: "Close",
  cancel: "Later",
  ok: "OK",
  duration: {
    hours: (h, m) => (m > 0 ? `${plural(h, "hour")} ${plural(m, "minute")}` : plural(h, "hour")),
    minutes: (m, s) => (s > 0 ? `${plural(m, "minute")} ${plural(s, "second")}` : plural(m, "minute")),
    seconds: (s) => plural(s, "second"),
  },
  errors: {
    notEnoughGold: "Not enough gold. Play a few more levels and you will have it!",
    owned: "You already have this one.",
    badCode: "That code is not right. Check each letter carefully for us.",
    slowDown: "Easy there, take a little break and try again.",
    resting: "This feature is resting. You can keep playing as usual.",
    network: "Could not connect. Keep playing, we will try again soon.",
    content: "This level needs a little update.",
    achievement: "The server has not seen this achievement yet. Give it a moment.",
    other: "Oops, something went a bit wrong. Try again later!",
  },
};
